import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  UncontrolledDropdown,
} from "reactstrap";
import classnames from "classnames";
import { Link, useSearchParams } from "react-router-dom";
import { DateTime } from "components/common/Date";
import StatusBadge from "components/common/StatusBadge";
import {
  PurchaseOrderStatus,
  PurchaseOrderStatusMap,
  purchaseOrderCanCancel,
  purchaseOrderCanConfirm,
} from "../constants/purchase-order.constants";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import ConfirmModal from "../PurchaseOrderList/ApproveModal";
import CancelModal from "../PurchaseOrderList/CancelModal";
import { toast } from "react-toastify";
import {
  cancelPurchaseOrder,
  confirmPurchaseOrder,
} from "../services/PurchaseOrderService";
import OverviewTab from "./OverviewTab";
import ItemsAndReceiptsTab from "./ItemsAndReceiptsTab";
import { usePurchaseOrderOverview } from "./PurchaseOrderOverviewContext";
import {
  isValidPoOverviewTabParam,
  poOverviewActiveTabId,
  PurchaseOrderOverviewTabParam,
  searchParamForPoOverviewTabId,
} from "./overviewTabSearchParams";

const Section = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { purchaseOrder: record, refetch } = usePurchaseOrderOverview();
  const { can } = useAccess();
  const canConfirm = can(Permissions.PURCHASE_ORDER.APPROVE);
  const canCancel = can(Permissions.PURCHASE_ORDER.CANCEL);
  const canUpdate = can(Permissions.PURCHASE_ORDER.UPDATE);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const tabQuery = searchParams.get("tab");

  useEffect(() => {
    if (tabQuery && !isValidPoOverviewTabParam(tabQuery)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("tab");
          return next;
        },
        { replace: true },
      );
    }
  }, [tabQuery, setSearchParams]);

  const setTabInUrl = (tabId: string) => {
    const param = searchParamForPoOverviewTabId(tabId);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (!param || param === PurchaseOrderOverviewTabParam.OVERVIEW) {
          next.delete("tab");
        } else {
          next.set("tab", param);
        }
        return next;
      },
      { replace: true },
    );
  };

  const handleConfirm = async () => {
    if (!record) return;
    try {
      const res = await confirmPurchaseOrder(record.id);
      if (res.success) {
        toast.success(res.message);
        setConfirmOpen(false);
        await refetch();
      } else toast.error(res.message);
    } catch (err) {
      toast.error(
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Confirm failed",
      );
    }
  };

  const handleCancel = async () => {
    if (!record) return;
    try {
      const res = await cancelPurchaseOrder(record.id);
      if (res.success) {
        toast.success(res.message);
        setCancelOpen(false);
        await refetch();
      } else toast.error(res.message);
    } catch (err) {
      toast.error(
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Cancel failed",
      );
    }
  };

  if (!record) {
    return (
      <Row>
        <Col lg={12}>
          <p className="text-muted mb-0">
            This purchase order could not be loaded.
          </p>
          <Link
            to="/inventory-management/purchase-orders"
            className="btn btn-light btn-sm mt-2"
          >
            Back to list
          </Link>
        </Col>
      </Row>
    );
  }

  const st = record.status;
  const displayTitle = record.code?.trim() || `PO #${record.id}`;
  const activeTab = poOverviewActiveTabId(tabQuery);

  return (
    <React.Fragment>
      <ConfirmModal
        show={confirmOpen}
        onConfirmClick={() => void handleConfirm()}
        onCloseClick={() => setConfirmOpen(false)}
        record={record}
      />
      <CancelModal
        show={cancelOpen}
        onCancelClick={() => void handleCancel()}
        onCloseClick={() => setCancelOpen(false)}
        record={record}
      />

      <Row>
        <Col lg={12}>
          <Card className="mt-n4 mx-n4">
            <div className="bg-light-subtle">
              <CardBody className="pb-0 px-4">
                <Row className="mb-3">
                  <div className="col-md">
                    <div>
                      <h4 className="fw-bold">{displayTitle}</h4>
                      <div className="hstack gap-3 flex-wrap">
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-muted">Status:</span>
                          <StatusBadge
                            value={st}
                            map={PurchaseOrderStatusMap}
                          />
                        </div>
                        {record.created_at ? (
                          <>
                            <div className="vr" />
                            <div>
                              Created:{" "}
                              <span className="fw-medium">
                                <DateTime date={record.created_at} />
                              </span>
                            </div>
                          </>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-auto">
                    <div className="hstack gap-2 flex-nowrap">
                      {canUpdate && st === PurchaseOrderStatus.DRAFT ? (
                        <Link
                          to={`/inventory-management/purchase-orders/edit/${record.id}`}
                          className="btn btn-light btn-sm border"
                        >
                          <i className="ri-pencil-line align-bottom me-1" />
                          Edit
                        </Link>
                      ) : null}
                      {canConfirm && purchaseOrderCanConfirm(st) ? (
                        <Button
                          type="button"
                          color="success"
                          size="sm"
                          className="btn-label"
                          onClick={() => setConfirmOpen(true)}
                        >
                          <i className="ri-checkbox-circle-line label-icon align-middle fs-14" />
                          Confirm
                        </Button>
                      ) : null}
                      {canCancel && purchaseOrderCanCancel(st) ? (
                        <UncontrolledDropdown>
                          <DropdownToggle
                            type="button"
                            className="btn btn-light btn-sm border"
                          >
                            <i className="ri-more-2-fill align-middle" />
                          </DropdownToggle>
                          <DropdownMenu end>
                            <DropdownItem
                              className="text-danger"
                              onClick={() => setCancelOpen(true)}
                            >
                              <i className="ri-close-circle-line align-bottom me-2" />
                              Cancel
                            </DropdownItem>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      ) : null}
                    </div>
                  </div>
                </Row>

                <Nav className="nav-tabs-custom border-bottom-0" role="tablist">
                  <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "1" },
                        "text-body fw-semibold",
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        setTabInUrl("1");
                      }}
                      href="#"
                    >
                      Overview
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "2" },
                        "text-body fw-semibold",
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        setTabInUrl("2");
                      }}
                      href="#"
                    >
                      Line Items
                    </NavLink>
                  </NavItem>
                </Nav>
              </CardBody>
            </div>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col lg={12}>
          <TabContent activeTab={activeTab} className="text-muted">
            <TabPane tabId="1">
              <OverviewTab />
            </TabPane>
            <TabPane tabId="2">
              <ItemsAndReceiptsTab />
            </TabPane>
          </TabContent>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default Section;
